import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Project, Template } from '../types';
import dayjs from 'dayjs';

const { Title } = Typography;
const { TextArea } = Input;

const ProjectList: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [projectsData, templatesData] = await Promise.all([
        api.getProjects(),
        api.getTemplates(),
      ]);
      setProjects(projectsData);
      setTemplates(templatesData);
    } catch (error) {
      message.error('Failed to load projects');
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingProject(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (project: Project) => {
    setEditingProject(project);
    form.setFieldsValue(project);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await api.deleteProject(id);
      message.success('Project deleted');
      loadData();
    } catch (error) {
      message.error('Failed to delete project');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingProject) {
        await api.updateProject(editingProject.id, values);
        message.success('Project updated');
      } else {
        const project = await api.createProject(values);
        // If template selected, create checklist
        if (values.templateId) {
          await api.createChecklist({
            projectId: project.id,
            templateId: values.templateId,
            name: `${project.name} - Due Diligence`,
          });
        }
        message.success('Project created');
      }
      setModalVisible(false);
      loadData();
    } catch (error) {
      message.error('Failed to save project');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'completed':
        return 'blue';
      case 'on_hold':
        return 'orange';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      title: 'Project Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Project) => (
        <span style={{ cursor: 'pointer', color: '#1890ff' }} onClick={() => navigate(`/projects/${record.id}`)}>{text}</span>
      ),
    },
    {
      title: 'Location',
      key: 'location',
      render: (_: any, record: Project) =>
        record.city && record.state ? `${record.city}, ${record.state}` : '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{status.replace('_', ' ').toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Checklists',
      key: 'checklists',
      render: (_: any, record: Project) => record.checklists?.length || 0,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => dayjs(date).format('MMM D, YYYY'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Project) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/projects/${record.id}`)}
          />
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm
            title="Delete this project?"
            description="This will also delete all checklists and items."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>
          Projects
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          New Project
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={projects}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingProject ? 'Edit Project' : 'New Project'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Project Name"
            rules={[{ required: true, message: 'Please enter project name' }]}
          >
            <Input placeholder="Enter project name" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Project description" />
          </Form.Item>

          <Form.Item name="address" label="Address">
            <Input placeholder="Property address" />
          </Form.Item>

          <Space style={{ width: '100%' }} size="large">
            <Form.Item name="city" label="City" style={{ marginBottom: 0, flex: 1 }}>
              <Input placeholder="City" />
            </Form.Item>
            <Form.Item name="state" label="State" style={{ marginBottom: 0, flex: 1 }}>
              <Input placeholder="State" />
            </Form.Item>
          </Space>

          {!editingProject && (
            <Form.Item
              name="templateId"
              label="Checklist Template"
              style={{ marginTop: 24 }}
            >
              <Select placeholder="Select a template to create checklist" allowClear>
                {templates.map((template) => (
                  <Select.Option key={template.id} value={template.id}>
                    {template.name} ({template.itemCount || 290} items)
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {editingProject && (
            <Form.Item name="status" label="Status" style={{ marginTop: 24 }}>
              <Select>
                <Select.Option value="active">Active</Select.Option>
                <Select.Option value="completed">Completed</Select.Option>
                <Select.Option value="on_hold">On Hold</Select.Option>
              </Select>
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default ProjectList;
